import React, { useState } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { 
  LayoutDashboard, 
  Users, 
  FileCheck, 
  Wallet, 
  Brain,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Shield,
  BarChart3,
  FileText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AdminOverview } from '@/components/admin/AdminOverview';
import { AdminUsers } from '@/components/admin/AdminUsers';
import { AdminMemberList } from '@/components/admin/AdminMemberList';
import { AdminKYC } from '@/components/admin/AdminKYC';
import { AdminLoans } from '@/components/admin/AdminLoans';
import { AdminAIInsights } from '@/components/admin/AdminAIInsights';
import { SupervisorApprovals } from '@/components/admin/SupervisorApprovals';
import { AdminSamwuStatistics } from '@/components/admin/AdminSamwuStatistics';
import { AdminApplications } from '@/components/admin/AdminApplications';

const AFRIPERX_LOGO = "https://customer-assets-agu9un31.emergentagent.net/job_c9a69f8c-f09a-4a22-b109-5dc5ad9f5a15/artifacts/qtk9sn35_MEDX-AI-Welcome-Poster.pptx%20%281%29.png";

const navItems = [
  { path: '/admin', label: 'Overview', icon: LayoutDashboard, exact: true },
  { path: '/admin/members', label: 'All Members', icon: Users },
  { path: '/admin/applications', label: 'Applications', icon: FileText },
  { path: '/admin/analytics', label: 'Analytics', icon: BarChart3 },
  { path: '/admin/kyc', label: 'KYC Approvals', icon: FileCheck },
  { path: '/admin/loans', label: 'Loans', icon: Wallet },
  { path: '/admin/ai', label: 'AI Insights', icon: Brain },
  { path: '/admin/approvals', label: 'Supervisor Approvals', icon: Shield, supervisorOnly: true },
];

export const AdminDashboardPage = () => {
  const { admin, adminLogout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === item.path;
    }
    return location.pathname.startsWith(item.path);
  };

  const handleLogout = () => {
    adminLogout();
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className={`
        fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 shadow-sm
        transform transition-transform duration-300 lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <img src={AFRIPERX_LOGO} alt="AfriPerx" className="h-9 w-auto" />
              <button 
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden text-gray-500 hover:text-gray-700"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mt-2 text-xs text-afriperx-gold font-medium">
              {admin?.role === 'supervisor' ? 'Supervisor Portal' : 'Admin Portal'}
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1">
            {navItems.filter(item => !item.supervisorOnly || admin?.role === 'supervisor').map((item) => {
              const Icon = item.icon;
              const active = isActive(item);
              
              return (
                <button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path);
                    setSidebarOpen(false);
                  }}
                  className={`
                    w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors
                    ${active 
                      ? 'bg-afriperx-green/10 text-afriperx-green font-medium' 
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }
                  `}
                  data-testid={`admin-nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                  {active && <ChevronRight className="w-4 h-4 ml-auto" />}
                </button>
              );
            })}
          </nav>

          {/* Admin info & Logout */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-afriperx-green/10 flex items-center justify-center">
                <span className="text-afriperx-gold font-bold">
                  {admin?.name?.charAt(0) || 'A'}
                </span>
              </div>
              <div>
                <p className="text-gray-900 font-medium">{admin?.name}</p>
                <p className="text-xs text-gray-500">{admin?.email}</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="w-full justify-start text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              data-testid="admin-logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/30 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main content */}
      <main className="lg:ml-64 min-h-screen">
        {/* Mobile header */}
        <header className="lg:hidden flex items-center justify-between p-4 bg-white border-b border-gray-200">
          <button 
            onClick={() => setSidebarOpen(true)}
            className="text-gray-600 hover:text-gray-900"
            data-testid="admin-menu-btn"
          >
            <Menu className="w-6 h-6" />
          </button>
          <img src={AFRIPERX_LOGO} alt="AfriPerx" className="h-7 w-auto" />
          <div className="w-6" />
        </header>

        <div className="p-4 md:p-6 lg:p-8">
          <Routes>
            <Route index element={<AdminOverview />} />
            <Route path="members" element={<AdminMemberList />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="applications" element={<AdminApplications />} />
            <Route path="analytics" element={<AdminSamwuStatistics />} />
            <Route path="kyc" element={<AdminKYC />} />
            <Route path="loans" element={<AdminLoans />} />
            <Route path="ai" element={<AdminAIInsights />} />
            <Route path="approvals" element={<SupervisorApprovals />} />
          </Routes>
        </div>
      </main>
    </div>
  );
};
